import React, { Suspense, useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import CookieConsent from "@components-shared/CookieConsent";
import { Footer } from "@components/footer/mobile";
import { GlobalLoader } from "@components/global-loader/GlobalLoader.mobile";
import { Header } from "@components/header/mobile";
import { OnBoardingFooter } from "@features/membership/mobile/registration-types/shared";
import { useScrollDirectionByElement } from "@hooks/useScrollDirectionByElement";
import { MainHtmlElementContext } from "@state/context/main-html/MainHtmlElementContext";
import { SuspendHeaderClassContext } from "@state/context/main-html/SuspendHeaderClassContext";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import useSafeAreaListener from "@utils/specific/capacitor/useSafeAreaListener";
import classnames from "classnames";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { getCurrentCulture } from "@utils";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const ScorePredictorFooter = lazy(loadFailPath, () => import("@features/score-predictor/mobile/footer"));

interface AppLayoutProps {
	children: React.ReactNode;
	hasOnboardingStyling?: boolean;
}

const AppLayout = ({ children, hasOnboardingStyling }: AppLayoutProps) => {
	const { pathname, search } = useLocation();
	const [mainRef, setMainRef] = useState<HTMLElement | null>(null);
	const { padding: safeAreaPadding, top: safeAreaTop } =
		useSafeAreaListener();

	const { scrollDirection, setShouldApplyHeaderClass } =
		useScrollDirectionByElement({
			targetRef: mainRef,
			threshold: 6,
		});

	var isLoginPage =
		pathname.includes("login") && !search.includes("isErrorPage");

	var isRegistration =
		pathname.includes("register") && !search.includes("isErrorPage");

	var isAuthPage =
		pathname.includes("auth") &&
		!search.includes("isErrorPage") &&
		!pathname.includes("/change-shop") &&
		!pathname.includes("/activate-wallet");

	var isRegistrationResponsePage =
		pathname.includes("registration/success") ||
		pathname.includes("registration/error");

	var isErrorPage =
		(pathname.includes("error") || search.includes("isErrorPage")) &&
		!isRegistrationResponsePage;

	var isAccountActivationCongratulations = pathname.includes(
		"account-activation-congratulations"
	);

    var isScorePredictorPage =
        pathname.includes("score-predictor")

	useEffect(() => {
		isLoginPage =
			pathname.includes("login") && !search.includes("isErrorPage");
		isRegistration =
			pathname.includes("register") && !search.includes("isErrorPage");

		isAuthPage =
			pathname.includes("auth") && !search.includes("isErrorPage");

		isRegistrationResponsePage =
			pathname.includes("registration/success") ||
			pathname.includes("registration/error");

		isErrorPage =
			pathname.includes("error") || search.includes("isErrorPage");

		isAccountActivationCongratulations = pathname.includes(
			"account-activation-congratulations"
		);
	}, [pathname, search]);

	const appClassName = classnames(
		"l--root l__app",
		{
			"l--root--auth l__app--auth": !isErrorPage && isAuthPage,
			"l--root--error l__app--error": isErrorPage,
			"is-capacitor no-select": isCapacitorPlatform(),
		},
		`is-${scrollDirection}`
	);

	return (
		<SuspendHeaderClassContext.Provider value={setShouldApplyHeaderClass}>
			<MainHtmlElementContext.Provider value={mainRef}>
				<div
					className={appClassName}
					style={{ padding: safeAreaPadding }}
				>
					{isCapacitorPlatform() && (
						<div
							className="l__header__overlay"
							style={{ height: `${safeAreaTop}px` }}
						></div>
					)}
					<Suspense fallback={<GlobalLoader />}>
						<main
							className="l--master l__main"
							ref={(newRef) => setMainRef(newRef)}
						>
							<Header />
							{children}
							{isAuthPage && (
								<OnBoardingFooter
									isRegistration={isRegistration}
									hasOnboardingStyling={
										hasOnboardingStyling && isAuthPage
									}
								/>
							)}
						</main>

						{!isAuthPage && !isScorePredictorPage && <Footer />}
                        {isScorePredictorPage && (
                            <ScorePredictorFooter
                                scrollDirection={scrollDirection}
                            />
                        )}
						<CookieConsent />
					</Suspense>
				</div>
			</MainHtmlElementContext.Provider>
		</SuspendHeaderClassContext.Provider>
	);
};

export default AppLayout;
